import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@apollo/client';
import classNames from 'classnames';

import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { RCButton, RCIconButton, RCPopover, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { DownloadIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing, core_apimessages_BulkExportListingsRequest_ListingState } from '@reverbdotcom/commons/src/rql';
import { isEmpty, isNull, isUndefined, omitBy, toPlainObject } from 'lodash';
import { useIsMobileWindowWidth } from '@reverbdotcom/cadence/components/helpers/is-mobile';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

export const COMPONENT_NAME = 'ExportListingsButton';
interface IExternalProps {
  selectedListings: Listing[];
  onSuccessCallback: (errorMessage: string) => void;
  onErrorCallback: (errorMessage: string) => void;
  fullSizeButton?: boolean;
}

const EXPORT_LISTINGS_MUTATION = gql`
  mutation Core_MyListings_ExportListings($input: Input_core_apimessages_BulkExportListingsRequest) {
    bulkExportListings(input: $input) {
      enqueued
      message
    }
  }
`;

export default function ExportListingsButton({
  selectedListings,
  onSuccessCallback,
  onErrorCallback,
  fullSizeButton = false,
}: IExternalProps) {
  const isMobile = useIsMobileWindowWidth();

  const [bulkExportListings, {
    data = {},
    loading,
    errors,
  }] = useMutation(EXPORT_LISTINGS_MUTATION);

  const { message: successMessage } = data.bulkExportListings ?? {};

  const hasErrors = !!errors.length;

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const hasSelectedListings = !!selectedListings.length;

  React.useEffect(() => {
    if (loading) return;

    setIsPopoverOpen(false);

    if (hasErrors) return onErrorCallback(
      I18n.t('discovery.listingsManagement.genericError'),
    );

    if (isEmpty(data)) return;

    onSuccessCallback(successMessage);
  }, [loading]);

  function trackDropdownItemClick({ state = null, listingIds = null }) {
    const eventAttributes = toPlainObject(omitBy({
      eventName: MParticleEventName.ExportingListings,
      componentName: COMPONENT_NAME,
      listingState: state,
      listingIds,
    }, isNull || isUndefined));

    trackEvent(eventAttributes);
  }

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, true);

  return (
    <RCPopover
      isOpen={isPopoverOpen}
      onDismiss={() => setIsPopoverOpen(false)}
      trapFocus
      position="bottom-end"
      anchor={fullSizeButton ? (
        <RCButton
          fullWidth
          onClick={() => setIsPopoverOpen(true)}
          variant="outlined"
          size={isMobile ? 'medium' : 'mini'}
        >
          <RCTextWithIcon
            svgComponent={DownloadIcon}
            placement="right"
          >
            {I18n.t('discovery.listingsManagement.toolbar.bulkActions.exportListings')}
          </RCTextWithIcon>
        </RCButton>
      ) : (
        <RCIconButton
          onClick={() => {
            trackEvent({
              eventName: MParticleEventName.ClickedComponent,
              componentName: COMPONENT_NAME,
            });

            setIsPopoverOpen(true);
          }}
          label={I18n.t('discovery.listingsManagement.toolbar.bulkActions.exportListings')}
          svgComponent={DownloadIcon}
          variant="outlined"
          size="mini"
        />
      )}
    >
      <div className="d-flex fx-dir-col fx-align-start weight-bold">
        <div className="pb-3 width-100">
          <button
            type="button"
            className="listings-management-export-buttons"
            onClick={() => {
              trackDropdownItemClick({
                state: core_apimessages_BulkExportListingsRequest_ListingState.ALL,
              });

              bulkExportListings({
                variables: {
                  input: {
                    state: core_apimessages_BulkExportListingsRequest_ListingState.ALL,
                  },
                },
              });
            }}
          >
            <RCTextWithIcon svgComponent={DownloadIcon}>
              {I18n.t('discovery.listingsManagement.toolbar.bulkActions.exports.exportAll')}
            </RCTextWithIcon>
          </button>
        </div>

        <div className={classNames('pt-3 bdt-1 bd-color-gray-300 width-100', {
          'pb-3': hasSelectedListings,
        })}>
          <button
            type="button"
            className="listings-management-export-buttons"
            onClick={() => {
              trackDropdownItemClick({
                state: core_apimessages_BulkExportListingsRequest_ListingState.DRAFT,
              });

              bulkExportListings({
                variables: {
                  input: {
                    state: core_apimessages_BulkExportListingsRequest_ListingState.DRAFT,
                  },
                },
              });
            }}
          >
            <RCTextWithIcon svgComponent={DownloadIcon}>
              {I18n.t('discovery.listingsManagement.toolbar.bulkActions.exports.exportAllDrafts')}
            </RCTextWithIcon>
          </button>
        </div>

        {hasSelectedListings && (
          <div className="pt-3 bdt-1 bd-color-gray-300 width-100">
            <button
              type="button"
              className="listings-management-export-buttons"
              onClick={() => {
                const listingIds = selectedListings.map(listing => listing.id);

                trackDropdownItemClick({
                  listingIds,
                });

                bulkExportListings({
                  variables: {
                    input: {
                      listingIds,
                    },
                  },
                });
              }}
            >
              <RCTextWithIcon svgComponent={DownloadIcon}>
                {I18n.t('discovery.listingsManagement.toolbar.bulkActions.exports.exportSelected')}
              </RCTextWithIcon>
            </button>
          </div>
        )}
      </div>
    </RCPopover>
  );
}
